import React from "react";
import { ChevronRight, Store as StoreIcon, Calendar } from "lucide-react";
import { fmtINR, fmtDate } from "../utils";

// Invoice list table for a single store, filtered by metric (sales|purchase).
// Header shows store + period + grand total; rows are clickable into the
// invoice detail view.
export default function InvoiceTable({ data, onOpen }) {
  const { invoices, total_amount, total_qty, item, store, period, metric, count } = data;
  const isSales = metric === "sales";
  const partyLabel = isSales ? "Customer" : "Vendor";
  const tone = isSales
    ? { bg: "bg-[var(--dashboard-bg-success)]", text: "text-[var(--dashboard-green-dark)]", border: "border-[var(--dashboard-bg-success)]" }
    : { bg: "bg-[var(--dashboard-bg-warning)]", text: "text-[var(--dashboard-warning-dark)]", border: "border-[var(--dashboard-bg-warning)]" };

  return (
    <div className="mt-4">
      {/* Summary card */}
      <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm px-5 py-4 mb-3">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <StoreIcon size={16} className="text-[var(--dashboard-gray-500)]" />
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
                  Store
                </div>
                <div className="text-[13.5px] font-bold text-[var(--dashboard-gray-900)] font-mono">
                  {store?.store_code || "—"}
                </div>
                <div className="text-[11.5px] text-[var(--dashboard-gray-500)]">
                  {[store?.city, store?.state].filter(Boolean).join(" · ")}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-[var(--dashboard-gray-500)]" />
              <div>
                <div className="text-[10.5px] font-semibold uppercase tracking-wide text-[var(--dashboard-gray-500)]">
                  Period
                </div>
                <div className="text-[13.5px] font-semibold text-[var(--dashboard-gray-900)]">
                  {fmtDate(period?.from)} – {fmtDate(period?.to)}
                </div>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span
              className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text}`}
            >
              {item ? `Item ${item} — ` : "Total "}{isSales ? "Sales" : "Purchase"}
            </span>
            <div className={`font-mono text-[22px] font-bold mt-1 ${tone.text}`}>
              {fmtINR(total_amount)}
            </div>
            <div className="text-[11.5px] text-[var(--dashboard-gray-500)]">
              {count} invoice{count === 1 ? "" : "s"}
              {Number.isFinite(Number(total_qty))
                ? ` · ${Number(total_qty).toLocaleString("en-IN")} strips`
                : ""}
            </div>
          </div>
        </div>
      </div>

      {invoices.length === 0 ? (
        <EmptyTable />
      ) : (
        <div className="bg-white border border-[var(--dashboard-gray-200)] rounded-xl shadow-sm overflow-hidden">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--dashboard-bg-table-head)] border-b border-[var(--dashboard-gray-200)]">
                {/* Sales bills show the business-facing custom number;
                    GRNs have no custom-number concept. */}
                <Th>{isSales ? "Custom Invoice No." : "Invoice No."}</Th>
                <Th>Date</Th>
                <Th>{partyLabel}</Th>
                <Th>Payment</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Discount</Th>
                <Th className="text-right">Amount</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.invoice_id}
                  onClick={() => onOpen(inv)}
                  className="border-b border-[var(--dashboard-gray-200)] last:border-b-0 hover:bg-[var(--dashboard-bg-hover)] cursor-pointer"
                >
                  <Td>
                    <span className="font-mono font-semibold text-[var(--dashboard-gray-900)]">
                      {inv.invoice_no || inv.invoice_id}
                    </span>
                  </Td>
                  <Td>{fmtDate(inv.invoice_date)}</Td>
                  <Td>
                    <div className="font-semibold text-[var(--dashboard-gray-900)]">
                      {inv.party?.name || "—"}
                    </div>
                    {inv.party?.phone && (
                      <div className="text-[12px] text-[var(--dashboard-gray-500)] font-mono">
                        {inv.party.phone}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <PaymentBadge status={inv.payment_status} />
                  </Td>
                  <Td className="text-right font-mono text-[var(--dashboard-gray-500)]">
                    {Number.isFinite(Number(inv.qty)) ? Number(inv.qty).toLocaleString("en-IN") : "—"}
                  </Td>
                  <Td className="text-right font-mono text-[var(--dashboard-gray-500)]">
                    {inv.discount > 0 ? "− " + fmtINR(inv.discount) : "—"}
                  </Td>
                  <Td className="text-right font-mono font-bold">
                    {fmtINR(inv.amount)}
                    {item && inv.item_amount != null ? (
                      <div className="text-[11px] font-semibold text-[var(--dashboard-green-dark)]">
                        item {fmtINR(inv.item_amount)}
                      </div>
                    ) : null}
                  </Td>
                  <Td className="text-right text-[var(--dashboard-gray-400)]">
                    <ChevronRight size={14} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`text-left text-[11px] font-bold uppercase tracking-wide text-[var(--dashboard-gray-500)] px-4 py-2.5 ${className}`}
    >
      {children}
    </th>
  );
}
function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 text-[13.5px] align-top text-[var(--dashboard-gray-900)] ${className}`}>
      {children}
    </td>
  );
}

function PaymentBadge({ status }) {
  if (!status) return <span className="text-[12px] text-[var(--dashboard-gray-400)]">—</span>;
  const tone = {
    paid: { bg: "bg-[var(--dashboard-bg-success)]", text: "text-[var(--dashboard-success)]" },
    pending: { bg: "bg-[var(--dashboard-bg-warning)]", text: "text-[var(--dashboard-warning-dark)]" },
    partial: { bg: "bg-[var(--dashboard-bg-warning)]", text: "text-[var(--dashboard-warning-dark)]" },
    failed: { bg: "bg-[#FEF2F2]", text: "text-[var(--dashboard-danger)]" },
  };
  const key = String(status).toLowerCase();
  const t = tone[key] || { bg: "bg-[var(--dashboard-gray-100)]", text: "text-[var(--dashboard-gray-500)]" };
  return (
    <span
      className={`inline-block text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${t.bg} ${t.text}`}
    >
      {status}
    </span>
  );
}

function EmptyTable() {
  return (
    <div className="bg-white border border-dashed border-[var(--dashboard-gray-200)] rounded-xl py-14 text-center text-[var(--dashboard-gray-500)]">
      <div className="text-[26px] mb-2">🔍</div>
      <div className="text-[14px] font-bold text-[var(--dashboard-gray-900)]">No matching invoices</div>
      <div className="text-[12.5px] mt-1">Widen the date range or clear the search.</div>
    </div>
  );
}
